import React from 'react';
import {
  Box,
  FormHelperText,
  IconButton,
  InputAdornment,
  Popover,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

import { InputLabelBase } from '../InputLabelBase';
import { TextFieldBase } from '../TextFieldBase';
import { RetroDatePopoverBody } from './RetroDatePopoverBody';
import type { RetroDateValue, RetroPreset } from './retroDateField.types';
import { useRetroDateField } from './useRetroDateField';
import {
  SELECT_CONTENT_HEIGHT,
  SELECT_INPUT_PADDING,
} from '../../../themes/_base/_base.overrides';
import { UW_VIEW_SCOPE } from '../../../themes/viewScopes';

export type RetroDateFieldProps = {
  /** Committed value — a `YYYY-MM-DD` string or one of the preset values. */
  readonly value: RetroDateValue;
  readonly onChange: (value: RetroDateValue) => void;
  readonly presets?: RetroPreset[];
  readonly label?: string;
  readonly minDate?: Date;
  readonly maxDate?: Date;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly fullWidth?: boolean;
  readonly name?: string;
  /** Month the calendar opens on when no date is committed yet. */
  readonly defaultDate?: Date;
  /**
   * `default` matches the filled Select globals in the Prime X quote header.
   * `naked` matches the Prime 250 headers, whose globals are `variant="naked"`
   * Selects — no surface and no border, so a framed control looks foreign.
   * `inline` sits the control inside a dense row — a table cell or a coverage
   * line — where it must not carry its own background or fixed width.
   */
  readonly variant?: 'default' | 'naked' | 'inline';
  readonly 'data-testid'?: string;
};

/**
 * A retroactive-date control that can be typed into or picked from a calendar.
 *
 * Presentational: all value handling comes in through props, and the typed
 * entry behaviour lives in `useRetroDateField`.
 */
export const RetroDateField = ({
  value,
  onChange,
  presets = [],
  label,
  minDate,
  maxDate,
  disabled = false,
  required = false,
  fullWidth = false,
  name = 'retroactiveDate',
  defaultDate,
  variant = 'default',
  'data-testid': dataTestId,
}: RetroDateFieldProps) => {
  const classes = useStyles();
  const variantClasses = {
    default: classes.input,
    naked: classes.nakedInput,
    inline: classes.inlineInput,
  };
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const generatedId = React.useId();
  const inputId = `${name}-${generatedId}`;
  const messageId = `${inputId}-message`;

  const {
    text,
    error,
    hint,
    placeholder,
    isCalendarOpen,
    isDirty,
    changeText,
    commitText,
    commitOnBlur,
    revert,
    selectDate,
    selectPreset,
    openCalendar,
    closeCalendar,
  } = useRetroDateField({ value, presets, minDate, maxDate, onChange });

  /**
   * The guidance line only appears once there is something to say, so a dense
   * row of globals keeps its height until the user actually starts editing.
   */
  const message = resolveMessage({ error, hint, isDirty });

  const toggleCalendar = () => {
    if (isCalendarOpen) {
      closeCalendar();
      return;
    }

    openCalendar();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitText(text);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      revert();
    }
  };

  return (
    <Box ref={anchorRef} className={classes.root}>
      {label && (
        <InputLabelBase htmlFor={inputId} required={required}>
          {label}
        </InputLabelBase>
      )}

      <TextFieldBase
        id={inputId}
        name={name}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        fullWidth={fullWidth}
        error={error ?? ''}
        showErrorMessage={false}
        showErrorPlace={false}
        data-testid={dataTestId}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          changeText(event.target.value)
        }
        onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
          commitOnBlur(event.target.value)
        }
        onKeyDown={handleKeyDown}
        inputProps={{
          inputMode: 'numeric',
          autoComplete: 'off',
          spellCheck: false,
          title: hint,
          'aria-invalid': Boolean(error),
          'aria-describedby': message ? messageId : undefined,
        }}
        InputProps={{
          // The rounded surface below replaces the standard variant's
          // underline; keeping both reads as a stray border.
          disableUnderline: true,
          className: variantClasses[variant],
          endAdornment: (
            <InputAdornment position="end" className={classes.adornment}>
              {/*
                A real button rather than a role-tagged icon: it has to answer
                Enter and Space, which an SVG with `role="button"` does not.
              */}
              <IconButton
                size="small"
                disabled={disabled}
                aria-label={isCalendarOpen ? 'Hide calendar' : 'Show calendar'}
                aria-expanded={isCalendarOpen}
                className={classes.calendarButton}
                onClick={toggleCalendar}
              >
                <CalendarToday
                  className={classes.calendarIcon}
                  color="primary"
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {message && (
        <FormHelperText
          id={messageId}
          error={Boolean(error)}
          role={error ? 'alert' : undefined}
          className={classes.message}
        >
          {message}
        </FormHelperText>
      )}

      <Popover
        open={isCalendarOpen}
        anchorEl={anchorRef.current}
        onClose={closeCalendar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <RetroDatePopoverBody
          selected={parseSelectedDate(value)}
          selectedValue={value}
          presets={presets}
          defaultDate={defaultDate}
          minDate={minDate}
          maxDate={maxDate}
          onSelectDate={selectDate}
          onSelectPreset={selectPreset}
        />
      </Popover>
    </Box>
  );
};

function resolveMessage({
  error,
  hint,
  isDirty,
}: {
  readonly error: string | null;
  readonly hint: string;
  readonly isDirty: boolean;
}): string | null {
  if (error) {
    return error;
  }

  if (isDirty) {
    return hint;
  }

  return null;
}

/** The calendar highlights the committed value, never a half-typed entry. */
function parseSelectedDate(value: RetroDateValue): Date | null {
  if (typeof value !== 'string') {
    return null;
  }

  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * In the underwriter view the field sits among Selects: their content height,
 * not MUI's taller input line, so it is exactly as tall; and an ellipsis, so a
 * narrow cell does not cut "Full Prior Acts" mid-letter. Other pages keep the
 * field as it was.
 */
const UW_INPUT_SCOPE = `${UW_VIEW_SCOPE} .MuiInputBase-input`;
const uwInputLine = {
  height: SELECT_CONTENT_HEIGHT,
  textOverflow: 'ellipsis',
};

/**
 * The theme gives every `.MuiInputBase-input` an 11px pad plus a 1px solid
 * border. The pad is what sizes the Select globals beside us (MuiSelect uses
 * 11.25px), so it is kept — but the border is dropped and the surface moves up
 * to the root, so the rounded background wraps the calendar trigger too.
 */
const inputElementReset = {
  border: 'none',
  background: 'transparent',
  padding: SELECT_INPUT_PADDING,
  paddingRight: 0,
  '&:focus': {
    background: 'transparent',
  },
};

const useStyles = makeStyles(({ palette, config }) => {
  /**
   * A retro field can be typed into, which is not obvious when it is styled to
   * sit quietly among Selects — especially the borderless `naked` variant.
   * Hover hints at it and focus commits to it. Both use an inset shadow rather
   * than a border so revealing the frame never nudges the layout by a pixel.
   */
  const editableAffordance = {
    cursor: 'text',
    '&:hover': {
      boxShadow: `inset 0 0 0 1px ${palette.divider}`,
    },
    '&:focus-within': {
      backgroundColor: palette.background.active,
      boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
    },
  };

  return {
    root: {
      position: 'relative',
    },
    /** Matches the SelectBase globals it sits alongside in the quote header. */
    input: {
      backgroundColor: palette.background.active,
      borderRadius: 5,
      minWidth: '5.5rem',
      paddingRight: '0.5rem',
      fontSize: config.textSizes.normal,
      ...editableAffordance,
      '& .MuiInputBase-input': inputElementReset,
      [UW_INPUT_SCOPE]: uwInputLine,
    },
    /**
     * For headers whose other globals are borderless `naked` Selects
     * (Prime 250). No surface and no frame at rest — the control reads as plain
     * text with a calendar affordance, exactly like the Selects beside it — but
     * it still has to announce itself as editable on hover and focus.
     */
    nakedInput: {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 5,
      minWidth: '5.5rem',
      paddingRight: '0.5rem',
      fontSize: config.textSizes.normal,
      ...editableAffordance,
      '& .MuiInputBase-input': inputElementReset,
      [UW_INPUT_SCOPE]: uwInputLine,
    },
    /** Coverage rows supply their own surface, so the control stays transparent. */
    inlineInput: {
      backgroundColor: 'inherit',
      borderRadius: 5,
      maxWidth: '8.2rem',
      paddingRight: '0.25rem',
      fontSize: config.textSizes.normal,
      ...editableAffordance,
      '& .MuiInputBase-input': inputElementReset,
      [UW_INPUT_SCOPE]: uwInputLine,
    },
    /**
     * Sits the trigger inside the rounded surface rather than over its edge.
     * In the underwriter view its height stays MUI's own `0.01em`: an
     * adornment that takes its content's height makes the calendar button set
     * the row's, and the field stood taller than the Selects beside it.
     * It is centred in the same rule: the globals row stretches every
     * adornment, and a stretched item with a fixed height drops to the top
     * edge, which put the button half outside the field.
     */
    adornment: {
      marginLeft: '0.25rem',
      height: 'auto',
      [UW_VIEW_SCOPE]: { height: '0.01em', alignSelf: 'center' },
    },
    /** Kept tight so the trigger does not inflate the control's height. */
    calendarButton: {
      padding: '0.1rem',
    },
    calendarIcon: {
      fontSize: config.textSizes.tertia,
      display: 'block',
    },
    message: {
      marginTop: '0.15rem',
      fontSize: config.textSizes.normal,
      lineHeight: 1.4,
    },
  };
});
